import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { cmsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Save } from 'lucide-react';

const emptyFooterLink = { label: '', href: '' };
const emptySocialLink = { label: '', url: '', icon_key: '' };

const AdminFooterSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    footer_text: '',
    primary_links: [],
    secondary_links: [],
    social_links: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await cmsApi.getFooter();
        setData(res);
      } catch (error) {
        console.error('Failed to load footer settings:', error);
        toast.error('Footer ayarlar\u0131 y\u00fcklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const updateField = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const updateListItem = (listField, index, newItem) => {
    setData((prev) => ({
      ...prev,
      [listField]: prev[listField].map((item, i) => (i === index ? newItem : item)),
    }));
  };

  const addListItem = (listField, template) => {
    setData((prev) => ({
      ...prev,
      [listField]: [...prev[listField], template],
    }));
  };

  const removeListItem = (listField, index) => {
    setData((prev) => ({
      ...prev,
      [listField]: prev[listField].filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await cmsApi.updateFooter(data);
      toast.success('Footer ayarlar\u0131 g\u00fcncellendi');
    } catch (error) {
      console.error('Failed to save footer settings:', error);
      toast.error('Footer ayarlar\u0131 kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-foreground">Footer Ayarlar\u0131</h1>
          <p className="text-sm text-muted-foreground">
            Footer a\u00e7\u0131klama metni, linkler ve sosyal medya hesaplar\u0131n\u0131 buradan y\u00f6net.
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>A\u00e7\u0131klama Metni</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={data.footer_text}
            onChange={(e) => updateField('footer_text', e.target.value)}
            rows={3}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>\u0130\u00e7erik Linkleri</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {data.primary_links.map((link, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1 space-y-2">
                  <Label className="text-xs">Metin</Label>
                  <Input
                    value={link.label}
                    onChange={(e) =>
                      updateListItem('primary_links', index, { ...link, label: e.target.value })
                    }
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <Label className="text-xs">URL</Label>
                  <Input
                    value={link.href}
                    onChange={(e) =>
                      updateListItem('primary_links', index, { ...link, href: e.target.value })
                    }
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mt-6 text-destructive"
                  onClick={() => removeListItem('primary_links', index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addListItem('primary_links', emptyFooterLink)}
            >
              <Plus className="h-4 w-4 mr-2" /> Link Ekle
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Site Linkleri</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {data.secondary_links.map((link, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1 space-y-2">
                  <Label className="text-xs">Metin</Label>
                  <Input
                    value={link.label}
                    onChange={(e) =>
                      updateListItem('secondary_links', index, { ...link, label: e.target.value })
                    }
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <Label className="text-xs">URL</Label>
                  <Input
                    value={link.href}
                    onChange={(e) =>
                      updateListItem('secondary_links', index, { ...link, href: e.target.value })
                    }
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mt-6 text-destructive"
                  onClick={() => removeListItem('secondary_links', index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addListItem('secondary_links', emptyFooterLink)}
            >
              <Plus className="h-4 w-4 mr-2" /> Link Ekle
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sosyal Medya</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {data.social_links.map((link, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
              <div className="space-y-2">
                <Label className="text-xs">Etiket</Label>
                <Input
                  value={link.label}
                  onChange={(e) =>
                    updateListItem('social_links', index, { ...link, label: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">URL</Label>
                <Input
                  value={link.url}
                  onChange={(e) =>
                    updateListItem('social_links', index, { ...link, url: e.target.value })
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 space-y-2">
                  <Label className="text-xs">Icon Key</Label>
                  <Input
                    placeholder="x, linkedin, substack, youtube"
                    value={link.icon_key}
                    onChange={(e) =>
                      updateListItem('social_links', index, { ...link, icon_key: e.target.value })
                    }
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-destructive"
                  onClick={() => removeListItem('social_links', index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addListItem('social_links', emptySocialLink)}
          >
            <Plus className="h-4 w-4 mr-2" /> Sosyal Hesap Ekle
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminFooterSettings;
